/**
 * Consultation service for managing video consultation operations
 * Requires component reference to access state, socket, and methods
 * Uses Twilio Video SDK
 */

class ConsultationService {
  constructor(component) {
    this.component = component;
    this.videoRoom = null;
    this.localVideoTrack = null;
    this.consultUserId = null;
    this.consultTimeoutRef = null;
  }

  /**
   * Get authentication token from sessionStorage
   * @returns {string|null} JWT token or null
   */
  getAuthToken() {
    try {
      const userStr = sessionStorage.getItem("user");
      if (!userStr) {
        return null;
      }
      
      const user = JSON.parse(userStr);
      return (
        user &&
        user.signInUserSession &&
        user.signInUserSession.idToken &&
        user.signInUserSession.idToken.jwtToken
      );
    } catch (error) {
      return null;
    }
  }

  /**
   * Fetch Twilio Video token
   * @param {string} identity - User identity (customer name or pharmacist name)
   * @param {string} roomName - Room name (pickup code)
   * @param {number} retryCount - Number of retry attempts
   * @returns {Promise<string|null>} Twilio token or null if failed
   */
  async fetchTwilioToken(identity, roomName, retryCount = 3) {
    console.log("[ConsultationService] 🔑 fetchTwilioToken called", {
      identity,
      roomName,
      retryCount,
      baseURL: process.env.REACT_APP_BASEURL,
    });
    
    const token = this.getAuthToken();
    if (!token) {
      console.error("[ConsultationService] ❌ No auth token available for Twilio token request");
      return null;
    }

    const url = `${process.env.REACT_APP_BASEURL}twilioToken/${identity}/${roomName}`;
    console.log("[ConsultationService] 📡 Request URL:", url);

    for (let attempt = 0; attempt < retryCount; attempt++) {
      try {
        console.log("[ConsultationService] 📤 Sending Twilio token request", {
          attempt: attempt + 1,
          url,
          identity,
          roomName,
          hasAuthToken: !!token,
          authTokenLength: token.length,
        });
        
        const response = await fetch(url, {
          method: "GET",
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        console.log("[ConsultationService] 📥 Twilio token response received", {
          attempt: attempt + 1,
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          headers: Object.fromEntries(response.headers.entries()),
        });

        if (!response.ok) {
          // Try to get error details from response
          let errorDetails = null;
          try {
            const errorText = await response.text();
            console.error("[ConsultationService] ❌ Error response body:", errorText);
            try {
              errorDetails = JSON.parse(errorText);
            } catch (e) {
              errorDetails = errorText;
            }
          } catch (e) {
            console.warn("[ConsultationService] ⚠️ Could not read error response body");
          }
          
          console.error("[ConsultationService] ❌ Twilio token request failed", {
            attempt: attempt + 1,
            status: response.status,
            statusText: response.statusText,
            errorDetails,
            url,
          });
          
          throw new Error(`HTTP error! status: ${response.status}${errorDetails ? ` - ${JSON.stringify(errorDetails)}` : ""}`);
        }

        const data = await response.json();
        console.log("[ConsultationService] 📦 Twilio token response data:", {
          hasData: !!data,
          hasToken: !!(data && data.token),
          dataKeys: data ? Object.keys(data) : [],
        });
        
        const twilioToken = data.token || null;
        if (twilioToken) {
          console.log("[ConsultationService] ✅ Twilio token fetched successfully", {
            identity,
            roomName,
            attempt: attempt + 1,
            tokenLength: twilioToken.length,
            tokenPrefix: twilioToken.substring(0, 20) + "...",
          });
        } else {
          console.warn("[ConsultationService] ⚠️ Twilio token not found in response", {
            data,
          });
        }
        return twilioToken;
      } catch (error) {
        console.error(`[ConsultationService] ❌ Error fetching Twilio token (attempt ${attempt + 1}):`, {
          error,
          errorMessage: error.message,
          errorStack: error.stack,
          url,
          identity,
          roomName,
        });
        
        if (attempt < retryCount - 1) {
          console.log(`[ConsultationService] ⏳ Retrying in 1 second... (${attempt + 2}/${retryCount})`);
          await new Promise((resolve) => setTimeout(resolve, 1000));
        } else {
          console.error("[ConsultationService] ❌ All retry attempts failed for Twilio token");
        }
      }
    }

    console.error("[ConsultationService] ❌ Failed to fetch Twilio token after all retries");
    return null;
  }

  /**
   * Send push notification for consultation
   * @param {Object} consultData - Consultation data object
   * @returns {Promise<string|null>} Consultation notification ID or null
   */
  async pushConsultNotification(consultData) {
    console.log("[ConsultationService] 📤 pushConsultNotification called", {
      customerName: consultData.User_name,
      puCode: consultData.resource_id,
      accountId: consultData.accounts_id,
      boxId: consultData.box_id,
    });
    
    const token = this.getAuthToken();
    if (!token) {
      console.error("[ConsultationService] ❌ No auth token available for push notification");
      return null;
    }

    try {
      const url = `${process.env.REACT_APP_BASEURL}fcm/pushConsultNotification`;
      console.log("[ConsultationService] 📡 Sending POST request to:", url);
      
      const response = await fetch(url, {
        method: "POST",
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(consultData),
      });

      console.log("[ConsultationService] 📥 Push notification response received", {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[ConsultationService] ❌ Push notification failed", {
          status: response.status,
          statusText: response.statusText,
          errorText,
        });
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("[ConsultationService] 📦 Push notification response data:", data);
      
      const consultUserId = (data && data.data && data.data.user_consult_notification_id) || null;
      
      if (consultUserId) {
        console.log("[ConsultationService] ✅ Push notification sent successfully", {
          consultUserId,
        });
      } else {
        console.warn("[ConsultationService] ⚠️ Push notification response missing user_consult_notification_id", {
          data,
        });
      }
      
      return consultUserId;
    } catch (error) {
      console.error("[ConsultationService] ❌ Error sending push notification:", error);
      return null;
    }
  }

  /**
   * Update consultation status
   * @param {string} consultUserId - Consultation notification ID
   * @param {string} status - Status (e.g., "Accepted", "Completed")
   * @returns {Promise<boolean>} Success status
   */
  async updateConsultationStatus(consultUserId, status) {
    const token = this.getAuthToken();
    if (!token) {
      console.error("No auth token available");
      return false;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}updateStatusOnSuccess`,
        {
          method: "POST",
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_consult_notification_id: consultUserId,
            is_call_status: status,
          }),
        }
      );

      return response.ok;
    } catch (error) {
      console.error("Error updating consultation status:", error);
      return false;
    }
  }

  /**
   * End consultation session
   * @param {string} consultUserId - Consultation notification ID
   * @param {boolean} isConsultEndCall - True if pharmacist ended
   * @param {boolean} isUserEndCall - True if user ended
   * @param {string} callStatus - Status ("Completed" or "disconnected")
   * @returns {Promise<boolean>} Success status
   */
  async endConsultSession(consultUserId, isConsultEndCall, isUserEndCall, callStatus) {
    const token = this.getAuthToken();
    if (!token) {
      console.error("No auth token available");
      return false;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}endconsultsession`,
        {
          method: "POST",
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_consult_notification_id: consultUserId,
            is_consult_end_call: isConsultEndCall,
            is_user_end_call: isUserEndCall,
            is_call_status: callStatus,
          }),
        }
      );

      return response.ok;
    } catch (error) {
      console.error("Error ending consultation session:", error);
      return false;
    }
  }

  /**
   * Mark Twilio room as completed
   * @param {string} puCode - Pickup code
   * @returns {Promise<boolean>} Success status
   */
  async markRoomCompleted(puCode) {
    const token = this.getAuthToken();
    if (!token) {
      console.error("No auth token available");
      return false;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}twilioToken/${puCode}/completed`,
        {
          method: "GET",
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.ok;
    } catch (error) {
      console.error("Error marking room as completed:", error);
      return false;
    }
  }

  /**
   * Get consultation notifications
   * @param {string} consultUserId - Consultation notification ID
   * @returns {Promise<Object|null>} Notification data or null
   */
  async getNotifications(consultUserId) {
    const token = this.getAuthToken();
    if (!token) {
      console.error("No auth token available");
      return null;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}getNotifications?consultuserId=${consultUserId}`,
        {
          method: "GET",
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error getting notifications:", error);
      return null;
    }
  }

  /**
   * Delete consultation notification
   * @param {string} boxId - Box ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteNotification(boxId) {
    const token = this.getAuthToken();
    if (!token) {
      console.error("No auth token available");
      return false;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}notificationdelete/${boxId}`,
        {
          method: "POST",
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.ok;
    } catch (error) {
      console.error("Error deleting notification:", error);
      return false;
    }
  }

  /**
   * Save missed consultation
   * @param {Object} consultData - Consultation data
   * @returns {Promise<boolean>} Success status
   */
  async saveMissedConsultation(consultData) {
    const token = this.getAuthToken();
    if (!token) {
      console.error("No auth token available");
      return false;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}saveuserconsult`,
        {
          method: "POST",
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(consultData),
        }
      );

      return response.ok;
    } catch (error) {
      console.error("Error saving missed consultation:", error);
      return false;
    }
  }

  /**
   * Start video consultation
   * @param {Array<string|number>} orderIds - Array of order IDs
   * @returns {Promise<boolean>} Success status
   */
  async startVideoConsultation(orderIds) {
    console.log("[ConsultationService] 🚀 startVideoConsultation called", {
      orderIds,
      orderCount: orderIds ? orderIds.length : 0,
      timestamp: new Date().toISOString(),
    });
    
    try {
      // Dynamic import for twilio-video (may not be installed yet)
      console.log("[ConsultationService] 📦 Importing twilio-video package...");
      let connect, createLocalVideoTrack;
      try {
        const twilioVideo = await import("twilio-video");
        connect = twilioVideo.connect;
        createLocalVideoTrack = twilioVideo.createLocalVideoTrack;
        console.log("[ConsultationService] ✅ twilio-video package imported successfully");
      } catch (importError) {
        console.error("[ConsultationService] ❌ Failed to import twilio-video:", importError);
        throw new Error("twilio-video package not installed. Please install it: npm install twilio-video");
      }

      // Get order data
      const { patientOrders, orderFullDetailsMap, popupData } = this.component.state;
      const orders = (patientOrders || []).filter((order) => orderIds.includes(order.id));
      
      if (orders.length === 0) {
        throw new Error("No valid orders found for consultation");
      }

      const firstOrder = orders[0];
      const fullDetails = orderFullDetailsMap[firstOrder.id] || firstOrder;
      const puCode = fullDetails.pucode || fullDetails.pickup_code || firstOrder.pucode || firstOrder.pickup_code;
      const customerName = fullDetails.customerName || (fullDetails.customer && fullDetails.customer.name) || (popupData && popupData.customerName) || "Customer";
      
      if (!puCode) {
        throw new Error("Pickup code not found for consultation");
      }

      // Get box and account details
      const box = fullDetails.box || fullDetails.boxDetails || {};
      const account = fullDetails.account || fullDetails.accDetails || {};
      const boxId = fullDetails.box_id || box.id || this.component.state.box_id;
      const accountId = fullDetails.parent_account_id || account.id || "";

      // Fetch Twilio token
      console.log("[ConsultationService] 🔑 Fetching Twilio token...", {
        customerName,
        puCode,
        orderIds,
        orderCount: orders.length,
      });
      
      const twilioToken = await this.fetchTwilioToken(customerName, puCode);
      if (!twilioToken) {
        console.error("[ConsultationService] ❌ Failed to fetch Twilio token");
        throw new Error("Failed to fetch Twilio token");
      }

      console.log("[ConsultationService] ✅ Token received, connecting to Twilio room...", {
        puCode,
        tokenLength: twilioToken.length,
        roomName: puCode,
      });

      // Connect to Twilio room
      console.log("[ConsultationService] 🔌 Attempting to connect to room:", puCode);
      const room = await connect(twilioToken, {
        name: puCode,
        audio: true,
        video: true,
      });

      console.log("[ConsultationService] ✅ Successfully connected to Twilio room", {
        roomName: room.name,
        roomSid: room.sid,
        localParticipant: room.localParticipant.identity,
        state: room.state,
      });

      this.videoRoom = room;

      // Create local video track
      console.log("[ConsultationService] 📹 Creating local video track...");
      try {
        const localTrack = await createLocalVideoTrack();
        console.log("[ConsultationService] ✅ Local video track created successfully", {
          trackId: localTrack.id,
          kind: localTrack.kind,
          isEnabled: localTrack.isEnabled,
        });
        
        this.localVideoTrack = localTrack;
        
        // Update component state with local track
        if (this.component.setState) {
          this.component.setState({ localVideoTrack: localTrack });
          console.log("[ConsultationService] ✅ Local video track added to component state");
        }
      } catch (videoError) {
        console.warn("[ConsultationService] ⚠️ Could not create local video track, continuing with audio only:", videoError);
        // Continue with audio only
      }

      // Prepare consultation data
      const ordersPickupCode = orders.map((order) => {
        const orderDetails = orderFullDetailsMap[order.id] || order;
        return orderDetails.pucode || orderDetails.pickup_code || order.pucode || order.pickup_code;
      }).filter(Boolean);

      const consultData = {
        User_name: customerName,
        is_consult_accepted: false,
        is_consult_end_call: false,
        is_user_end_call: false,
        resource_id: puCode,
        consult_start_date: new Date(),
        consult_end_date: null,
        is_call_status: "Pending",
        auth_token: "",
        accounts_id: accountId,
        store_name: account.name || "",
        box_id: boxId,
        kiosk_name: box.name || "",
        is_releaseorder: false,
        orders_pickup_codes: JSON.stringify(ordersPickupCode),
      };

      // Send push notification
      console.log("[ConsultationService] 📤 Sending push notification to pharmacist...", {
        customerName,
        puCode,
        accountId,
        boxId,
        orderCount: ordersPickupCode.length,
      });
      
      const consultUserId = await this.pushConsultNotification(consultData);
      if (consultUserId) {
        console.log("[ConsultationService] ✅ Push notification sent successfully", {
          consultUserId,
        });
      } else {
        console.warn("[ConsultationService] ⚠️ Push notification returned no user ID");
      }
      
      this.consultUserId = consultUserId;

      // Configure socket for consultation
      if (this.component.socket && boxId) {
        console.log("[ConsultationService] 🔌 Configuring socket for consultation...", {
          boxId,
          puCode,
          socketConnected: this.component.socket.connected,
        });
        
        // Subscribe to pickup code updates
        this.component.socket.emit("KioskSubscribe", {
          pucode: puCode,
          message: "update Status For Connected Twilio",
        });
        console.log("[ConsultationService] 📡 Emitted KioskSubscribe event", { puCode });

        // Configure box ID
        this.component.socket.emit("ConfigureBoxId", {
          box_id: boxId,
        });
        console.log("[ConsultationService] 📡 Emitted ConfigureBoxId event", { boxId });
      } else {
        console.warn("[ConsultationService] ⚠️ Socket not available or boxId missing", {
          hasSocket: !!this.component.socket,
          boxId,
        });
      }

      // Emit socket event
      if (this.component.socket) {
        const videoConsultPayload = {
          pucode: puCode,
          message: "Hi from Counter Socket",
          account_id: accountId || null,
        };
        console.log("[ConsultationService] 📡 Emitting videoConsult socket event...", videoConsultPayload);
        this.component.socket.emit("videoConsult", videoConsultPayload);
        console.log("[ConsultationService] ✅ videoConsult event emitted");
      } else {
        console.warn("[ConsultationService] ⚠️ Socket not available for videoConsult event");
      }

      // Set up room event handlers
      console.log("[ConsultationService] 🎧 Setting up room event handlers...");
      this.setupRoomEventHandlers(room, puCode);
      console.log("[ConsultationService] ✅ Room event handlers configured");

      // Set timeout for consultation acceptance (45 seconds)
      console.log("[ConsultationService] ⏱️ Setting consultation timeout (45 seconds)...");
      this.consultTimeoutRef = setTimeout(() => {
        console.log("[ConsultationService] ⏰ Consultation timeout triggered");
        if (this.component.state && !this.component.state.isConsultAcceptedCall) {
          this.handleConsultTimeout(puCode);
        }
      }, 45000);

      // Update component state
      console.log("[ConsultationService] 🔄 Updating component state...", {
        isConsult: true,
        isVideoConsultation: true,
        consultPuCode: puCode,
        consultUserId: consultUserId,
        startRinging: true,
      });
      
      if (this.component.setState) {
        this.component.setState({
          isConsult: true,
          isVideoConsultation: true,
          consultPuCode: puCode,
          consultUserId: consultUserId,
          startRinging: true,
        });
        console.log("[ConsultationService] ✅ Component state updated successfully");
      } else {
        console.warn("[ConsultationService] ⚠️ Component setState not available");
      }

      console.log("[ConsultationService] ✅ Video consultation started successfully", {
        puCode,
        consultUserId,
        roomSid: room.sid,
        hasLocalTrack: !!this.localVideoTrack,
      });

      return true;
    } catch (error) {
      console.error("[ConsultationService] ❌ Error starting video consultation:", {
        error,
        errorMessage: error.message,
        errorStack: error.stack,
        orderIds,
      });
      
      if (this.component.setState) {
        const errorMessage = error.message || "Failed to start video consultation";
        console.log("[ConsultationService] 🔄 Setting error state:", errorMessage);
        this.component.setState({
          videoConsultationError: errorMessage,
          isConsult: false,
        });
      }
      return false;
    }
  }

  /**
   * Setup Twilio room event handlers
   * @param {Object} room - Twilio room instance
   * @param {string} puCode - Pickup code
   */
  setupRoomEventHandlers(room, puCode) {
    console.log("[ConsultationService] 🎧 Setting up room event handlers for room:", puCode);
    
    // Handle participant connection
    room.on("participantConnected", (participant) => {
      console.log("[ConsultationService] 👤 Participant connected to room", {
        participantIdentity: participant.identity,
        participantSid: participant.sid,
        puCode,
        roomSid: room.sid,
      });
      
      // Emit status update
      if (this.component.socket) {
        console.log("[ConsultationService] 📡 Emitting updateStatusForConnectedTwilioKiosk...");
        this.component.socket.emit("updateStatusForConnectedTwilioKiosk", {
          pucode: puCode,
          message: "update Status For Connected Twilio",
        });
        console.log("[ConsultationService] ✅ Status update event emitted");
      } else {
        console.warn("[ConsultationService] ⚠️ Socket not available for status update");
      }
      
      if (this.component.setState) {
        console.log("[ConsultationService] 🔄 Updating state: pharmacist connected");
        this.component.setState({
          isPharmacistConnected: true,
          isConsultAcceptedCall: true,
          startRinging: false,
        });
        console.log("[ConsultationService] ✅ State updated: pharmacist connected");
      }

      // Handle remote tracks
      participant.on("trackSubscribed", (track) => {
        console.log("[ConsultationService] 📹 Remote track subscribed", {
          trackId: track.id,
          trackKind: track.kind,
          participantIdentity: participant.identity,
        });
        
        if (track.kind === "video") {
          console.log("[ConsultationService] 📹 Remote video track received");
          // Attach remote video
          if (this.component.setState) {
            this.component.setState({ remoteVideoTrack: track });
            console.log("[ConsultationService] ✅ Remote video track added to state");
          }
        } else if (track.kind === "audio") {
          console.log("[ConsultationService] 🔊 Remote audio track received (handled automatically)");
          // Remote audio is handled automatically by Twilio
        }
      });
      
      participant.on("trackUnsubscribed", (track) => {
        console.log("[ConsultationService] 📹 Remote track unsubscribed", {
          trackId: track.id,
          trackKind: track.kind,
          participantIdentity: participant.identity,
        });
        
        track.detach();
        if (track.kind === "video" && this.component.setState) {
          console.log("[ConsultationService] 🔄 Removing remote video track from state");
          this.component.setState({ remoteVideoTrack: null });
          console.log("[ConsultationService] ✅ Remote video track removed");
        }
      });
      
      participant.on("trackSubscriptionFailed", (error) => {
        console.error("[ConsultationService] ❌ Track subscription failed", {
          error,
          participantIdentity: participant.identity,
        });
      });
    });

    // Handle participant disconnection
    room.on("participantDisconnected", (participant) => {
      console.log("[ConsultationService] 👤 Participant disconnected from room", {
        participantIdentity: participant.identity,
        participantSid: participant.sid,
        puCode,
      });
      
      if (this.component.setState) {
        console.log("[ConsultationService] 🔄 Updating state: pharmacist disconnected");
        this.component.setState({
          isPharmacistConnected: false,
        });
        console.log("[ConsultationService] ✅ State updated: pharmacist disconnected");
      }
    });

    // Handle room disconnection
    room.on("disconnected", (room, error) => {
      console.log("[ConsultationService] 🔌 Room disconnected", {
        roomName: room.name,
        roomSid: room.sid,
        puCode,
        error: error ? error.message : null,
      });
      
      if (error) {
        console.error("[ConsultationService] ❌ Room disconnection error:", error);
      }
      
      console.log("[ConsultationService] 🧹 Cleaning up video consultation...");
      this.cleanupVideoConsultation(puCode, true);
    });

    // Handle reconnection
    room.on("reconnecting", (error) => {
      console.log("[ConsultationService] 🔄 Reconnecting to room...", {
        puCode,
        error: error ? error.message : null,
      });
    });

    room.on("reconnected", () => {
      console.log("[ConsultationService] ✅ Reconnected to room", {
        roomName: room.name,
        roomSid: room.sid,
        puCode,
      });
    });
    
    room.on("trackStarted", (track) => {
      console.log("[ConsultationService] 📹 Track started", {
        trackId: track.id,
        trackKind: track.kind,
        isEnabled: track.isEnabled,
      });
    });
    
    room.on("trackStopped", (track) => {
      console.log("[ConsultationService] 📹 Track stopped", {
        trackId: track.id,
        trackKind: track.kind,
      });
    });
    
    console.log("[ConsultationService] ✅ All room event handlers registered", {
      puCode,
      roomSid: room.sid,
    });
  }

  /**
   * Handle consultation timeout
   * @param {string} puCode - Pickup code
   */
  async handleConsultTimeout(puCode) {
    if (this.consultTimeoutRef) {
      clearTimeout(this.consultTimeoutRef);
      this.consultTimeoutRef = null;
    }

    // Emit timeout event
    if (this.component.socket) {
      this.component.socket.emit("consultTimeoutKiosk", {
        pucode: puCode,
        message: "Call pickup timeout from Counter!",
      });
    }

    // Save missed consultation
    if (this.consultUserId) {
      const { patientOrders, orderFullDetailsMap } = this.component.state;
      const orders = patientOrders || [];
      const firstOrder = orders[0];
      const fullDetails = orderFullDetailsMap[(firstOrder && firstOrder.id)] || firstOrder || {};
      const account = fullDetails.account || fullDetails.accDetails || {};
      const box = fullDetails.box || fullDetails.boxDetails || {};

      const missedConsultData = {
        created_on: new Date(),
        consult_start_date: new Date(),
        consult_end_date: new Date(),
        pickup_code: puCode,
        user_name: fullDetails.customerName || "Customer",
        call_duration: null,
        user_id: null,
        consultation_name: "",
        Status: "Missed",
        accounts_id: fullDetails.parent_account_id || account.id || "",
        store_name: account.name || "",
        box_id: fullDetails.box_id || box.id || "",
        kiosk_name: box.name || "",
      };

      await this.saveMissedConsultation(missedConsultData);
    }

    // Cleanup
    this.cleanupVideoConsultation(puCode, false);

    if (this.component.setState) {
      this.component.setState({
        isConsultNotAvailable: true,
        startRinging: false,
      });
    }
  }

  /**
   * End video consultation
   * @param {boolean} isConsultEndCall - True if pharmacist ended
   * @param {boolean} isUserEndCall - True if user/pharmacist ended
   * @returns {Promise<boolean>} Success status
   */
  async endVideoConsultation(isConsultEndCall = false, isUserEndCall = true) {
    const puCode = (this.component.state && this.component.state.consultPuCode) || null;
    const isAccepted = (this.component.state && this.component.state.isConsultAcceptedCall) || false;

    // Stop local video track
    if (this.localVideoTrack) {
      this.localVideoTrack.stop();
      this.localVideoTrack.detach();
      this.localVideoTrack = null;
    }

    // Disconnect from room
    if (this.videoRoom) {
      this.videoRoom.disconnect();
      this.videoRoom = null;
    }

    // Mark room as completed
    if (puCode) {
      await this.markRoomCompleted(puCode);
    }

    // End session
    if (this.consultUserId) {
      await this.endConsultSession(
        this.consultUserId,
        isConsultEndCall,
        isUserEndCall,
        isAccepted ? "Completed" : "disconnected"
      );
    }

    // Emit socket event
    if (this.component.socket && puCode) {
      if (isUserEndCall && !isConsultEndCall) {
        this.component.socket.emit("endSessionKioskCancelConsult", {
          pucode: puCode,
          message: "Video call cancelled from Counter",
        });
      } else if (isAccepted) {
        this.component.socket.emit("endSessionKiosk", {
          pucode: puCode,
          message: "Video call disconnected from Counter",
        });
      }
    }

    // Clear timeout
    if (this.consultTimeoutRef) {
      clearTimeout(this.consultTimeoutRef);
      this.consultTimeoutRef = null;
    }

    // Update component state
    if (this.component.setState) {
      this.component.setState({
        isConsult: false,
        isVideoConsultation: false,
        isPharmacistConnected: false,
        isConsultAcceptedCall: false,
        startRinging: false,
        localVideoTrack: null,
        remoteVideoTrack: null,
        consultPuCode: null,
        consultUserId: null,
      });
    }

    return true;
  }

  /**
   * Cleanup video consultation
   * @param {string} puCode - Pickup code
   * @param {boolean} isAccepted - Whether consultation was accepted
   */
  async cleanupVideoConsultation(puCode, isAccepted) {
    await this.endVideoConsultation(false, true);
  }


  /**
   * Cleanup all consultation resources
   */
  cleanup() {
    if (this.videoRoom) {
      this.endVideoConsultation(false, true);
    }
    if (this.consultTimeoutRef) {
      clearTimeout(this.consultTimeoutRef);
      this.consultTimeoutRef = null;
    }
  }
}

export default ConsultationService;




// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/services/ConsultationService.js